import { ChevronDown, Monitor, Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation("settings");

  let icon: React.ReactNode;
  switch (theme) {
    case "light":
      icon = (
        <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
      );
      break;
    case "dark":
      icon = (
        <Moon className="h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
      );
      break;
    case "system":
      icon = <Monitor className="h-[1.2rem] w-[1.2rem]" />;
      break;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon">
          {icon}
          <span className="sr-only">{t("appearance.toggleTheme")}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => setTheme("light")}>
          <Sun className="mr-2 h-4 w-4" />
          {t("appearance.lightMode")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("dark")}>
          <Moon className="mr-2 h-4 w-4" />
          {t("appearance.darkMode")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("system")}>
          <Monitor className="mr-2 h-4 w-4" />
          {t("appearance.systemMode")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface ThemeSelectProps {
  className?: string;
}

export function ThemeSelect({ className }: ThemeSelectProps) {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation("settings");

  const themeOptions = [
    {
      value: "light",
      label: t("appearance.lightMode"),
      icon: Sun,
      description: t("descriptions.lightModeInfo"),
    },
    {
      value: "dark",
      label: t("appearance.darkMode"),
      icon: Moon,
      description: t("descriptions.darkModeInfo"),
    },
    {
      value: "system",
      label: t("appearance.systemMode"),
      icon: Monitor,
      description: t("descriptions.systemModeInfo"),
    },
  ];

  return (
    <div className={className}>
      <div className="grid grid-cols-1 gap-3">
        {themeOptions.map((option) => {
          const Icon = option.icon;
          const isSelected = theme === option.value;

          return (
            <button
              key={option.value}
              onClick={() => setTheme(option.value)}
              className={`w-full flex items-center space-x-4 p-4 rounded-lg border-2 transition-all hover:bg-gray-50 dark:hover:bg-gray-800 ${
                isSelected
                  ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950"
                  : "border-gray-200 dark:border-gray-700"
              }`}
            >
              <div
                className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  isSelected ? "bg-indigo-100 dark:bg-indigo-900" : "bg-gray-100 dark:bg-gray-800"
                }`}
              >
                <Icon
                  className={`w-6 h-6 ${
                    isSelected
                      ? "text-indigo-600 dark:text-indigo-400"
                      : "text-gray-600 dark:text-gray-400"
                  }`}
                />
              </div>
              <div className="flex-1 text-left">
                <div
                  className={`font-medium ${
                    isSelected
                      ? "text-indigo-900 dark:text-indigo-100"
                      : "text-gray-900 dark:text-gray-100"
                  }`}
                >
                  {option.label}
                </div>
                <div
                  className={`text-sm ${
                    isSelected
                      ? "text-indigo-600 dark:text-indigo-300"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {option.description}
                </div>
              </div>
              {isSelected && (
                <div className="w-5 h-5 bg-indigo-600 dark:bg-indigo-400 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Compact theme selector for mobile/space-constrained layouts
 */
export function CompactThemeSelect({ className }: ThemeSelectProps) {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation("settings");

  const themeOptions = [
    { value: "light", label: t("appearance.lightMode"), icon: Sun },
    { value: "dark", label: t("appearance.darkMode"), icon: Moon },
    { value: "system", label: t("appearance.systemMode"), icon: Monitor },
  ];

  const currentTheme = themeOptions.find((option) => option.value === theme) || themeOptions[2];
  const CurrentIcon = currentTheme.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="default" className={`gap-2 ${className || ""}`}>
          <CurrentIcon className="h-4 w-4" />
          <span>{currentTheme.label}</span>
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-36">
        {themeOptions.map((option) => {
          const Icon = option.icon;
          return (
            <DropdownMenuItem
              key={option.value}
              onClick={() => setTheme(option.value)}
              className="flex items-center cursor-pointer"
            >
              <Icon className="mr-2 h-4 w-4" />
              {option.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
